import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { EMPLOYEE_PRESENCE } from "@benefit-systems/hr-tools-ui";
import { TextField, Autocomplete, InputAdornment } from "@mui/material";
import { debounce, isEmpty } from "lodash";
import __ from "src/utils/Translations";
import EmployeesAutocompleteOption from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompleteOption";
import {
  ApproverType,
  IEmployeeDetails,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { getInputStyles } from "./EmployeesAutocomplete.styles";
import EmployeesAutocompleteAdornment from "./EmployeesAutocompleteAdornment";

interface IEmployeesAutocompletePreviewProps {
  name: string;
  label: string;
  approvers?: string[];
  disabled: boolean;
  required?: boolean;
}

const EmployeesAutocompletePreview: FC<IEmployeesAutocompletePreviewProps> = ({
  name,
  label,
  approvers = [],
  disabled = false,
  required = false,
}) => {
  const { control } = useFormContext();

  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRemovedAdornment, setIsRemovedAdornment] = useState(true);

  const filteredByApprovers = useMemo(() => {
    if (name !== "approver") {
      return HARDCODED_OPTIONS;
    }

    const approverTypeToEmployeeId = {
      [ApproverType.IMMEDIATE_SUPERIOR]: "1", // Bezpośredni przełożony
      [ApproverType.INTERMEDIATE_SUPERIOR]: "2", // Przełożony wyższego stopnia
      [ApproverType.AHR]: "3", // Administrator HR
    };

    const allowedEmployeeIds = approvers
      .map(
        (approverType) =>
          approverTypeToEmployeeId[
            approverType as keyof typeof approverTypeToEmployeeId
          ]
      )
      .filter(Boolean);

    return HARDCODED_OPTIONS.filter((option) =>
      allowedEmployeeIds.includes(option.employeeId)
    );
  }, [name, approvers]);

  const options = useMemo(() => {
    if (!search) return filteredByApprovers;

    const searchLower = search.toLowerCase();
    return filteredByApprovers.filter(
      (option) =>
        option.firstName.toLowerCase().includes(searchLower) ||
        option.lastName.toLowerCase().includes(searchLower)
    );
  }, [search, filteredByApprovers]);

  const debouncedSearch = useRef(
    debounce((__: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(debouncedSearch, [debouncedSearch]);

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({
          field: { value, onChange, ref: fieldRef },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            options={options}
            getOptionLabel={(option) =>
              !isEmpty(option) ? `${option?.firstName} ${option?.lastName}` : ""
            }
            getOptionKey={(option) => option.employeeId}
            isOptionEqualToValue={(option, selectedValue) =>
              option.employeeId === selectedValue?.employeeId
            }
            value={value || null}
            disableListWrap
            disablePortal
            disabled={disabled}
            onOpen={() => setSearch("")}
            onChange={(__, newValue) => {
              setIsTyping(false);

              setIsRemovedAdornment(!newValue);

              onChange(newValue);
            }}
            onBlur={() => setIsTyping(false)}
            onInputChange={(event, newInputValue, reason) => {
              if (reason !== "reset") {
                setIsTyping(true);
                handleSearchChange(event, newInputValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                id={name}
                label={label}
                required={required}
                error={invalid}
                helperText={
                  disabled
                    ? __(
                        "Wniosek zostanie zaakceptowany automatycznie. Akceptant nie jest wymagany."
                      )
                    : error?.message ||
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (error as any)?.presentAtQueriedTimeRange?.message
                }
                inputRef={fieldRef}
                sx={getInputStyles(isTyping, disabled)}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps?.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        {!isTyping && !isRemovedAdornment && (
                          <EmployeesAutocompleteAdornment data={value} />
                        )}
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>{(params.slotProps?.input as any)?.endAdornment}</>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <EmployeesAutocompleteOption liProps={props} option={option} />
            )}
            slotProps={{
              listbox: {
                style: {
                  maxHeight: "300px",
                  overflowY: "auto",
                },
              },
            }}
            data-testid={`absence-request-form-field__${name}`}
          />
        )}
      />
    </>
  );
};

export default EmployeesAutocompletePreview;

const HARDCODED_OPTIONS: IEmployeeDetails[] = [
  {
    employeeId: "1",
    firstName: "Bezpośredni",
    lastName: "przełożony",
    fkNumber: "123",
    initials: "",
    area: null,
    parentArea: null,
    additionalDisplayArea: null,
    localization: null,
    presenceStatus: EMPLOYEE_PRESENCE.PRESENT,
    position: "Team Leader",
    dailyWorkingHours: 0,
    email: null,
    phone: null,
    employmentType: null,
    active: true,
    contractFrom: null,
    contractTo: null,
    workingHours: {
      nominator: null,
      denominator: null,
    },
  },
  {
    employeeId: "2",
    firstName: "Przełożony wyższego",
    lastName: "stopnia",
    fkNumber: "124",
    initials: "",
    area: null,
    parentArea: null,
    additionalDisplayArea: null,
    localization: null,
    presenceStatus: EMPLOYEE_PRESENCE.PRESENT,
    position: "Kierownik działu",
    dailyWorkingHours: 0,
    email: null,
    phone: null,
    employmentType: null,
    active: true,
    contractFrom: null,
    contractTo: null,
    workingHours: {
      nominator: null,
      denominator: null,
    },
  },
  {
    employeeId: "3",
    firstName: "Administrator",
    lastName: "HR",
    fkNumber: "125",
    initials: "",
    area: null,
    parentArea: null,
    additionalDisplayArea: null,
    localization: null,
    presenceStatus: EMPLOYEE_PRESENCE.PRESENT,
    position: "Specjalista ds. HR",
    dailyWorkingHours: 0,
    email: null,
    phone: null,
    employmentType: null,
    active: true,
    contractFrom: null,
    contractTo: null,
    workingHours: {
      nominator: null,
      denominator: null,
    },
  },
  {
    employeeId: "4",
    firstName: "Współpracownik",
    lastName: "1",
    fkNumber: "126",
    initials: "",
    area: null,
    parentArea: null,
    additionalDisplayArea: null,
    localization: null,
    presenceStatus: EMPLOYEE_PRESENCE.PRESENT,
    position: "Specjalista",
    dailyWorkingHours: 0,
    email: null,
    phone: null,
    employmentType: null,
    active: true,
    contractFrom: null,
    contractTo: null,
    workingHours: {
      nominator: null,
      denominator: null,
    },
  },
  {
    employeeId: "5",
    firstName: "Współpracownik",
    lastName: "2",
    fkNumber: "127",
    initials: "",
    area: null,
    parentArea: null,
    additionalDisplayArea: null,
    localization: null,
    presenceStatus: EMPLOYEE_PRESENCE.PRESENT,
    position: "Specjalista",
    dailyWorkingHours: 0,
    email: null,
    phone: null,
    employmentType: null,
    active: true,
    contractFrom: null,
    contractTo: null,
    workingHours: {
      nominator: null,
      denominator: null,
    },
  },
  {
    employeeId: "6",
    firstName: "Współpracownik",
    lastName: "3",
    fkNumber: "128",
    initials: "",
    area: null,
    parentArea: null,
    additionalDisplayArea: null,
    localization: null,
    presenceStatus: EMPLOYEE_PRESENCE.PRESENT,
    position: "Specjalista",
    dailyWorkingHours: 0,
    email: null,
    phone: null,
    employmentType: null,
    active: true,
    contractFrom: null,
    contractTo: null,
    workingHours: {
      nominator: null,
      denominator: null,
    },
  },
];
